import type { LucideIcon } from 'lucide-react'
import type { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone'
import { Dropzone, cn } from '@teach/ui'
import { CanvasDropzone } from './CanvasDropzone'
import { UploadProgressBody } from './UploadProgressBody'
import { useSmoothProgress } from './useSmoothProgress'
import { useMessages } from '@/i18n'
import { messages } from './messages'

// Пустое состояние ImageUpload: только Dropzone под клик/drop. Поле «или вставьте URL»
// убрано по бэк-логу заказчика (п.3/14/20) — оно жило здесь и разом обслуживало все
// шесть мест загрузки, поэтому и снято одним местом.
interface Props {
  icon: LucideIcon
  label: string
  rootProps: DropzoneRootProps
  inputProps: DropzoneInputProps
  disabled: boolean
  /** Заполнить родителя целиком (родитель задаёт пропорции, например 16:9). */
  fill?: boolean
  /** Идёт загрузка: тот же спиннер «Загрузка…», что у всех зон загрузки. */
  uploading?: boolean
  /** Процент загрузки для подписи под спиннером. */
  progress?: number | null
  /** Своя подпись под заголовком (у баннера — про 16:9). */
  subtitle?: string
  /** `canvas` — на светлом экране урока (составной блок): цвета из `blockTheme`. */
  tone?: 'app' | 'canvas'
}

export const ImageUploadEmptyState = ({
  icon,
  label,
  rootProps,
  inputProps,
  disabled,
  fill = false,
  uploading = false,
  progress = null,
  subtitle: subtitleProp,
  tone = 'app',
}: Props) => {
  // Браузер отчитывается только об отправке байтов — без сглаживания подпись
  // сразу показывала 100%, пока сервер ещё обрабатывал файл.
  const m = useMessages(messages)
  const subtitle = subtitleProp ?? m.imageFormats
  const shown = useSmoothProgress(uploading ? (progress ?? 0) : null)
  return tone === 'canvas' ? (
    <CanvasDropzone
      icon={icon}
      label={label}
      subtitle={subtitle}
      rootProps={rootProps}
      inputProps={inputProps}
      disabled={disabled}
      progress={shown}
      uploading={uploading}
    />
  ) : (
    <div
      {...rootProps}
      className={cn(disabled ? 'cursor-not-allowed' : 'cursor-pointer', fill ? 'h-full' : '')}
    >
      <input {...inputProps} />
      {uploading ? (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border px-4 py-8',
            fill ? 'h-full' : '',
          )}
        >
          <UploadProgressBody
            percent={shown ?? 0}
            colors={{
              title: 'hsl(var(--foreground))',
              track: 'hsl(var(--muted))',
              fill: 'hsl(var(--brand))',
            }}
          />
        </div>
      ) : (
        <Dropzone
          icon={icon}
          title={label}
          subtitle={subtitle}
          className={fill ? 'flex h-full flex-col items-center justify-center' : undefined}
        />
      )}
    </div>
  )
}
